/**
 * /accounts/:aid/settings — single Settings page combining what used
 * to be Profile / Caregivers / Devices / Account into one scrollable
 * surface. The four section components live in components/settings/
 * and could be reused on a per-section page later if we ever want
 * deep-linkable sub-routes again.
 */

import { useParams } from 'react-router-dom';

import { useAccount } from '@/hooks/useAccount';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { ProfileSection } from '@/components/settings/ProfileSection';
import { YourProfileSection } from '@/components/settings/YourProfileSection';
import { CaregiversSection } from '@/components/settings/CaregiversSection';
import { DevicesSection } from '@/components/settings/DevicesSection';
import { OrdersSection } from '@/components/settings/OrdersSection';
import { InstallAppSection } from '@/components/settings/InstallAppSection';
import { AccountSection } from '@/components/settings/AccountSection';

export function SettingsPage() {
  const { aid } = useParams<{ aid: string }>();
  const { account, loading } = useAccount(aid ?? null);

  if (!aid) return null;
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const elderName = account?.elderName?.trim() || 'this person';
  const isInitialSetup = !!account && !account.elderName.trim();

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-ink">
          {isInitialSetup ? 'Tell us about the person you care for' : `Settings for ${elderName}`}
        </h1>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Your profile</CardTitle>
        </CardHeader>
        <CardBody>
          <YourProfileSection />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Their profile</CardTitle>
        </CardHeader>
        <CardBody>
          {/* Keyed by aid: Settings is a carry-over section, so switching
              elders re-renders this in place — without the remount, a pending
              debounced name edit (buffer + timer survive the re-render) would
              commit under the NEW aid, writing elder B's name onto elder A's
              account. Same defence as VoicePage's key={aid} wrapper. */}
          <ProfileSection key={aid} aid={aid} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Caregivers</CardTitle>
        </CardHeader>
        <CardBody>
          <CaregiversSection aid={aid} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Buttons</CardTitle>
        </CardHeader>
        <CardBody>
          <DevicesSection aid={aid} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Orders</CardTitle>
        </CardHeader>
        <CardBody>
          <OrdersSection aid={aid} />
        </CardBody>
      </Card>

      {/* Owns its own Card so it vanishes entirely when already installed. */}
      <InstallAppSection />

      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardBody>
          <AccountSection aid={aid} />
        </CardBody>
      </Card>

      <p className="text-center text-xs text-ink-muted">
        <a
          href="https://remindmevoice.com/privacy"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-ink-soft"
        >
          Privacy
        </a>
        {' · '}
        <a
          href="https://remindmevoice.com/terms"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-ink-soft"
        >
          Terms
        </a>
        {' · '}
        <button
          type="button"
          className="underline hover:text-ink-soft"
          onClick={() => {
            // The banner script is cross-loaded from the marketing origin;
            // if it's blocked, fall back to the policy's cookies section
            // rather than a silent no-op.
            if (window.rmvCookies?.open) window.rmvCookies.open();
            else window.open('https://remindmevoice.com/privacy', '_blank', 'noreferrer');
          }}
        >
          Manage cookies
        </button>
      </p>
      <p className="text-center text-xs text-ink-muted">
        App version {__APP_VERSION__}
      </p>
    </div>
  );
}
